import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {finalize, first} from 'rxjs/operators';
import {UserService} from '../../../../../services/user-services/user.service';
import {Country, MatSelectCountryModule} from '@angular-material-extensions/select-country';
import {CountryService} from '../../../../../services/country.service';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {CityInputComponent} from '../city-input/city-input.component';
import {NgxIntlTelInputModule} from '@justin-s/ngx-intl-tel-input';
import {MatInputModule} from '@angular/material/input';
import {NgClass, NgIf} from '@angular/common';
import {MatFormFieldModule} from '@angular/material/form-field';

@Component({
    selector: 'app-address-input',
    templateUrl: './address-input.component.html',
    styleUrls: ['./address-input.component.scss'],
    standalone: true,
    imports: [MatFormFieldModule, NgIf, MatInputModule, FormsModule, ReactiveFormsModule, NgxIntlTelInputModule, NgClass, MatSelectCountryModule, CityInputComponent, TranslateModule]
})
export class AddressInputComponent implements OnInit, AfterViewInit {

  @Input() prefill: string;
  @Input() mode?: string;
  @Input() adressType: string;
  @Input() placeholder?: string;
  @Input() countryRestriction; // jSON object ; ex: {country: 'fr' } - if not set, doesn't affect autocomplete
  @Input() needFormattedAddress:boolean = false;
  @Input() showManualInput: boolean = true;
  @Input() address2FormControl: UntypedFormControl;

  @Output() formattedAddress: EventEmitter<any> = new EventEmitter<any>();
  // Get la partie "adresse complémentaire" de l'autocomplete (non manuel)
  @Output() getAdditionalAddress: EventEmitter<any> = new EventEmitter<any>();
  @Output() isFetchingDatas: EventEmitter<boolean> = new EventEmitter<boolean>();

  addressText;
  @ViewChild('addressText') set address(content: ElementRef) {
    if(content) {
      this.addressText = content;
    }
  }
  cityText;
  @ViewChild(CityInputComponent) set city(content: CityInputComponent) {
    if(content) {
      this.cityText = content;
    }
  }

  isFetchingData: boolean;
  pickedAddress: any;

  isManualInput: boolean = false;

  predefinedCountries : Country[];
  public manualAddressForm: UntypedFormGroup;
  public user;

  private geoCoder;

  constructor(
    private userService: UserService,
    private countryService: CountryService,
    private translate: TranslateService,
    private formBuilder: UntypedFormBuilder,
  ) { }


  // V2 autocomplete
  defaultAddressControl = new UntypedFormControl();
  @Input() addressControl: any;

  ngOnInit(): void {
    if (!this.placeholder) {
      this.placeholder = 'COMMON.ADDRESS';
    }
    if (!this.addressControl || (this.addressControl && this.addressControl.value && this.addressControl.value.trim().startsWith('null'))) {
      this.addressControl = this.defaultAddressControl;
    }
    if (!this.address2FormControl) {
      this.address2FormControl = new UntypedFormControl();
    }
  }

  ngAfterViewInit() {
    this.setPlaceAutocomplete();
  }

  private setPlaceAutocomplete() {
    const autocomplete = new google.maps.places.Autocomplete(this.addressText.nativeElement,{
      types: [this.adressType],  // 'establishment' / 'address' / 'geocode',
      componentRestrictions: this.countryRestriction,
      fields: ['address_components', 'formatted_address']
    });
    google.maps.event.addListener(autocomplete, 'place_changed', () => {
      const place = autocomplete.getPlace();
      if (place) {
        this.addressControl.setValue(place.formatted_address);
        this.getAddressFormattedForDB(place);
      }
    });
  }

  /**
   * Formate l'adresse en : n°, rue/route, ville, code postal, état, pays ; quelque soit le pays.
   * Utilisé pour le champ "address" d'un user.
   */
  private getAddressFormattedForDB(place) {
    this.isFetchingDatas.emit(true);
    this.isFetchingData = true;
    const pac = place['address_components'];
    let number = '', route = '', google_city = '', postal_code = '', region = '', country = '', sub2 = '', sub3 = '', sub4 = '', premise = '';
    pac.forEach(p => {
      if (pac[0].types.includes('premise')) {
        if (p.types.includes('sublocality_level_4')) sub4 = p.long_name;
        if (p.types.includes('sublocality_level_3')) sub3 = p.long_name;
        if (p.types.includes('sublocality_level_2')) sub2 = p.long_name;
        if (p.types.includes('premise')) premise = p.long_name;
        if (google_city.length === 0 && p.types.includes('locality')) google_city += p.long_name;
      } else {
        if (route.length === 0 && p.types.includes('route')) route = p.long_name;
        if (google_city.length === 0 && (p.types.includes('locality') || p.types.includes('sublocality') || p.types.includes('postal_town'))) google_city += p.long_name;
      }
      if (number.length === 0 && p.types.includes('street_number')) number = p.long_name + ', ';
      if (postal_code.length === 0 && p.types.includes('postal_code')) postal_code = p.long_name ;
      if (region.length === 0 && p.types.includes('administrative_area_level_1')) region = p.long_name;
      if (country.length === 0 && p.types.includes('country')) country = p.short_name;
    })
    if (pac[0].types.includes('premise')) {
      route = sub3 + '-' + sub4 + '-' + premise + ' ' + sub2 ;
    }
    this.userService.getCities(country)
      .pipe(first(),
        finalize(() => {
          this.addressControl.setErrors(null);
          this.pickedAddress = {
            number: number,
            route: route,
            city: google_city,
            fullHomeAddress: place.formatted_address ,
            postal_code: postal_code,
            region: region,
            country: country};
          this.isFetchingData = false;
          this.isFetchingDatas.emit(false);
          this.formattedAddress.emit(this.pickedAddress);
        }))
      .subscribe((response:any) => {
        google_city = response.find(city => { return city.name === google_city; });
        if(this.checkLatin('addressControlGoogle',  place.formatted_address)){
          this.addressControl.setErrors({'latin': true});
        }
      });
  }

  /**
   * Trigger quand le user écrit
   * Envoie l'adresse au composant parent
   * Evite d'avoir une data dans la var parent si le user a changé quelque chose ( = force autocomplete )
   */
  public onChange(event): void {
    this.pickedAddress = event.target.value;
    this.formattedAddress.emit(this.pickedAddress);
    if (this.pickedAddress) {
      this.addressControl.setErrors({notPicked: true});
    }
  }

  // Envoie au parent le contenu de "complément d'adresse" de l'autocomplete
  onChange2(e) {
    this.getAdditionalAddress.emit(e.target.value);
  }

  public goManual(value): void {
    this.isManualInput = value;
    if (value) {
      this.addressControl.setValidators();
      this.addressControl.updateValueAndValidity();
      if (!this.user) {
        this.user = this.userService.getInfos();
      }
      if (!this.manualAddressForm || (this.manualAddressForm && !this.manualAddressForm.controls.country)) {
        this.manualAddressForm = this.formBuilder.group({
          country_obj: [null, Validators.required],
          country: [null]
        })
      }
      if (!this.predefinedCountries || (this.predefinedCountries && this.predefinedCountries.length === 0)) {
        this.getCountries();
      }
      if (!this.geoCoder) {
        this.geoCoder = new google.maps.Geocoder();
      }
    } else {
      this.addressControl.setValidators([Validators.required]);
      this.addressControl.updateValueAndValidity();
      if (this.pickedAddress) {
        this.formattedAddress.emit(this.pickedAddress);
      }
    }
  }

  private getCountries(): void {
    this.predefinedCountries = [];
    this.predefinedCountries = this.countryService.getCountries();
    this.translate.get('COUNTRIES').subscribe(() => {
      this.predefinedCountries.forEach(element => {
        let code = element.alpha2Code ;
        element.name = this.translate.instant('COUNTRIES.' + code);
      });
    });
  }

  /**
   * Trigger quand le user selectionne un pays dans la liste déroulante
   * Créer, s'ils n'existent pas déjà, les formControl "city", "route" et "complément d'adresse"
   * Actualise la liste des villes si le user change de pays
   */
  public onCountrySelected(event: any): void {
    // Récupère les villes du pays sélectionner
    if (this.cityText) {
      this.cityText.getCities(event.alpha2Code);
    }

    this.user.homeCountry_Info = event;
    // Utilisation : display
    this.manualAddressForm.controls.country_obj.setValue(event);
    // Utilisation : send
    this.manualAddressForm.controls.country.setValue(event.alpha2Code);
    // Utilisation : display
    if (!this.manualAddressForm.controls.city_name) {
      this.manualAddressForm.addControl('city_name', this.formBuilder.control(null, [Validators.required]));
    }
    // Utilisation : display & send
    if (!this.manualAddressForm.controls.route) {
      this.manualAddressForm.addControl('route', this.formBuilder.control(null, [Validators.required]));
    }
    // Utilisation : display & send
    if (!this.manualAddressForm.controls.additional) {
      this.manualAddressForm.addControl('additional', this.formBuilder.control(''));
    }
    // Utilisation : send
    if (!this.manualAddressForm.controls.postal_code) {
      this.manualAddressForm.addControl('postal_code', this.formBuilder.control(null));
    }
    // Utilisation : send
    if (!this.manualAddressForm.controls.city) {
      this.manualAddressForm.addControl('city', this.formBuilder.control(null));
    }

    // Récupère le Zip code de la ville
    // Doc: https://developers.google.com/maps/documentation/javascript/geocoding
    // ref : https://stackoverflow.com/questions/16742908/how-to-convert-city-or-state-name-to-zipcode
    this.manualAddressForm.get('city_name').valueChanges.subscribe(value => {

      // Object si le user select dans le menu, sinon c'est quand il écrit
      if (typeof value == 'object') {
        this.manualAddressForm.controls.city_name.setValue(value.name);
        this.manualAddressForm.controls.city.setValue(value);
      } else {
        this.manualAddressForm.controls.city.setValue(null);
      }

      // Evite les call inutiles
      if (value && value.length > 1 && this.manualAddressForm.value !== value) {
        this.geoCoder.geocode({address: value,componentRestrictions: {country: this.user.homeCountry_Info ? this.user.homeCountry_Info.alpha2Code : null} }, (result, status) => {
          if (status == 'OK') {
            const postalCode = result[0].address_components.find(element => element.types.includes('postal_code'));
            if (postalCode) {
              this.manualAddressForm.controls.postal_code.setValue(postalCode.short_name)
            }
          }
        })
      }
    });

    // Envoie les datas au parent une fois tous les required remplis
    this.manualAddressForm.valueChanges.subscribe(value => {
      if (value.country && value.city && value.route) {
        if (value.additional) {
          value.additional = ' '+value.additional;
        }
        this.formattedAddress.emit(value);
      }

    })
  }

  public checkLatin(form,e){
    const  regex = new RegExp("^[A-zÀ-ÿ0-9- ']+$");
    if(form === "addressControlGoogle"){
      if (regex.test(e)) {
        this.addressControl.setErrors(null);
      }
      else {
        this.addressControl.setErrors({'latin': true});
      }
    }
    else {
      if (regex.test(e.target.value)) {
        switch (form) {
          case 'addressControl' : {
            this.addressControl.setErrors(null);
            break;
          }
          case 'address2FormControl' : {
            this.address2FormControl.setErrors(null);
            break;
          }
          case 'route' : {
            this.manualAddressForm.controls.route.setErrors(null);
            break;
          }
          case 'additional' : {
            this.manualAddressForm.controls.additional.setErrors(null);
            break;
          }
        }
        return true;
      } else {
        switch (form) {
          case 'addressControl' : {
            this.addressControl.setErrors({'latin': true});
            break;
          }
          case 'address2FormControl' : {
            this.addressControl.setErrors({'latin': true});
            break;
          }
          case 'route' : {
            this.manualAddressForm.controls.route.setErrors({'latin': true});
            break;
          }
          case 'additional' : {
            this.manualAddressForm.controls.additional.setErrors({'latin': true});
            break;
          }
        }
        return false
      }
    }
  }

}
